// src/components/workflow/ProjectCmntAndNote.tsx
import React, { useState, useMemo, useEffect } from 'react';
import { Card, Rate, Input, Button, Typography, Row, Col } from 'antd';
import { TWorkflowProject, TWorkflowProjectDetails } from '../../models/workflow/workflow';

const { Text, Paragraph } = Typography;

export interface ProjectCmntAndNoteProps {
  workflowProject?: TWorkflowProject;
  isEditable: boolean;
  onSubmit?: (updatedDetails: TWorkflowProjectDetails) => Promise<void> | void;
}

const ProjectCmntAndNote: React.FC<ProjectCmntAndNoteProps> = ({
  workflowProject,
  isEditable,
  onSubmit,
}) => {
  const details = workflowProject?.details
  const [coherence, setCoherence] = useState(details?.coherence_score ?? 0);
  const [codeQuality, setCodeQuality] = useState(details?.code_quality ?? 0);
  const [visualQuality, setVisualQuality] = useState(details?.visual_quality ?? 0);
  const [comment, setComment] = useState(details?.comment ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const isValid = coherence > 0 && codeQuality > 0 && visualQuality > 0 && comment.trim() !== '';

  // Sync local state when details prop changes
  useEffect(() => {
    setCoherence(details?.coherence_score ?? 0);
    setCodeQuality(details?.code_quality ?? 0);
    setVisualQuality(details?.visual_quality ?? 0);
    setComment(details?.comment ?? '');
  }, [details]);

  const formattedDate = useMemo(() => {
    if (!workflowProject?.created_at) return '';
    return new Date(workflowProject?.created_at).toLocaleString();
  }, [workflowProject?.created_at]);

  const handlePublish = async () => {
    if (!onSubmit) return;
    if (!isValid) {
      setError('Veuillez renseigner toutes les notes (≥ 1 ☆) et un commentaire.');
      return;
    }
    setError('');
    setSubmitting(true);
    await onSubmit({
      coherence_score: coherence,
      code_quality: codeQuality,
      visual_quality: visualQuality,
      comment,
    });
    setSubmitting(false);
  };

  return (
    <Card style={{ marginTop: 24 }}>
      <Row gutter={24}>
        {/* Colonne de gauche : notations */}
        <Col xs={24} md={12}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <Text>Cohérence du jeu</Text><br />
              <Rate
                count={5}
                value={coherence}
                onChange={isEditable ? setCoherence : undefined}
                disabled={!isEditable}
              />
              <Text> ({coherence}/5)</Text>
            </div>
            <div>
              <Text>Qualité du code</Text><br />
              <Rate
                count={5}
                value={codeQuality}
                onChange={isEditable ? setCodeQuality : undefined}
                disabled={!isEditable}
              />
              <Text> ({codeQuality}/5)</Text>
            </div>
            <div>
              <Text>Qualité visuelle</Text><br />
              <Rate
                count={5}
                value={visualQuality}
                onChange={isEditable ? setVisualQuality : undefined}
                disabled={!isEditable}
              />
              <Text> ({visualQuality}/5)</Text>
            </div>
          </div>
        </Col>

        {/* Colonne de droite : commentaire */}
        <Col xs={24} md={12}>
          {isEditable ? (
            <>
              <Input.TextArea
                rows={6}
                placeholder="Votre commentaire…"
                value={comment}
                onChange={e => setComment(e.target.value)}
              />
              <div style={{ textAlign: 'right', marginTop: 8 }}>
                <Button
                  type="primary"
                  loading={submitting}
                  onClick={handlePublish}
                  disabled={!isValid}
                >
                  Publier
                </Button>
              </div>
            </>
          ) : (
            <>
              <Paragraph>
                {comment ? (
                  comment
                ) : (
                  <Text type="secondary">Pas de commentaire</Text>
                )}
              </Paragraph>
              {formattedDate && (
                <Text type="secondary">Posté le {formattedDate}</Text>
              )}
            </>
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default ProjectCmntAndNote;
